import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { resolveFontSize } from '../config/fontSizes';
import { ExerciseBanner } from './ExerciseBanner';

export const MultipleChoiceExercise = ({ 
  exercise, 
  onAnswerSubmit, 
  onBack, 
  onExit,
  topic,
  totalCorrect = 0,
  totalIncorrect = 0,
  currentExerciseIndex = 0,
  totalExercises = 1
}) => {
  const { t } = useLanguage();
  
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);

  const question = exercise.question || '';
  // Normalize options source (prefer content.options, fallback to exercise.options)
  let options = exercise.content?.options || exercise.options || [];
  // Normalize each option to object { text, ... }
  options = options.map(o => (typeof o === 'string' ? { text: o } : (o || { text: '' })));

  // If options have 'order', apply stable sort by order to match editor/back-end ordering
  if (options.length > 0 && options[0].order !== undefined) {
    options = [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  // Determine correct answer index by matching stored correct answer text when possible.
  const storedCorrectText = (exercise.content?.correctAnswerText || exercise.content?.correctAnswer || exercise.correctAnswer || '').toString().trim().toLowerCase();
  let correctAnswerIndex = Number.isInteger(exercise.content?.correctAnswerIndex) ? exercise.content.correctAnswerIndex : (Number.isInteger(exercise.correctAnswerIndex) ? exercise.correctAnswerIndex : 0);
  if (storedCorrectText) {
    const found = options.findIndex(o => (o.text || '').toString().trim().toLowerCase() === storedCorrectText);
    if (found !== -1) correctAnswerIndex = found;
  }
  const questionToken = exercise.content?.fontSize || exercise.fontSize || 'text-2xl';
  const optionTokenMap = {
    'text-4xl': 'text-xl',
    'text-3xl': 'text-lg',
    'text-2xl': 'text-base',
    'text-xl': 'text-base',
    'text-lg': 'text-sm',
    'text-base': 'text-sm',
    'text-sm': 'text-sm',
    'text-xs': 'text-xs'
  };
  const questionFontSize = resolveFontSize(questionToken);
  const optionFontSize = resolveFontSize(optionTokenMap[questionToken] || 'text-base');
  // correctAnswerIndex теперь вычислен выше

  const handleAnswerSelect = (index) => {
    if (isAnswered) return;
    setSelectedAnswerIndex(index);
    setIsAnswered(true);
    const isCorrect = index === correctAnswerIndex;
    if (onAnswerSubmit) {
      onAnswerSubmit({ isCorrect, exerciseId: exercise.id });
    }
    setTimeout(() => { if (onBack) onBack(); }, 1500);
  };

  const getOptionClass = (index) => {
    if (!isAnswered) return 'mc-option';
    if (index === correctAnswerIndex) return 'mc-option correct-answer';
    if (index === selectedAnswerIndex && index !== correctAnswerIndex) return 'mc-option wrong-answer';
    return 'mc-option dimmed';
  };

  const getButtonIcon = (index) => {
    if (!isAnswered) return null;
    if (index === correctAnswerIndex) return <span style={{ fontSize: 22 }}>✓</span>;
    if (index === selectedAnswerIndex && index !== correctAnswerIndex) return <span style={{ fontSize: 22 }}>✗</span>;
    return null;
  };

  const progressPct = totalExercises > 0 ? ((currentExerciseIndex + 1) / totalExercises) * 100 : 0;

  return (
    <div className="exercise-page">
      {/* Unified Banner with progress bar */}
      <div style={{ margin: '0 auto 32px', maxWidth: 1000, padding: '0 24px' }}>
        <ExerciseBanner
          currentIndex={currentExerciseIndex}
          totalExercises={totalExercises}
          correct={totalCorrect}
          incorrect={totalIncorrect}
          onExit={onExit}
          showProgress={true}
        />
        {/* Progress bar */}
        <div className="exercise-progress-row" style={{ marginTop: 8 }}>
          <span className="exercise-progress-label">{t('lessonProgress')}:</span>
          <div className="exercise-progress-track">
            <div className="exercise-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="exercise-progress-text">{currentExerciseIndex + 1} / {totalExercises}</span>
        </div>
      </div>

      <div style={{ width: '100%', maxWidth: 680, margin: '0 auto' }}>
        {/* Основной контент */}
        <div className="mc-question-card">
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <p className="mc-prompt">
              {t('multipleChoiceTest') || 'Тест: множественный выбор'}
            </p>
            <div className="mc-question-text" style={{ color: exercise.content?.textColor, fontSize: questionFontSize }}>
              {question}
            </div>
            <p style={{ fontSize: 15, color: 'rgba(245,244,240,0.4)', fontWeight: 500, marginTop: 12 }}>
              {t('selectCorrectOption') || 'Выбери правильный вариант'}
            </p>
          </div>

          <div className="mc-options">
            {options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                disabled={isAnswered}
                className={getOptionClass(index)}
                style={{ color: exercise.content?.textColor, fontSize: optionFontSize }}
              >
                {getButtonIcon(index)}
                {option.text || option}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultipleChoiceExercise;
